import { createContext, createElement, useState, useEffect, useRef, useCallback, useContext, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { User, Session } from '@supabase/supabase-js';
import { StoreUser, StoreUserRole, StorePermission, AuthStatus } from '@/types/shop';
import { toast } from '@/hooks/use-toast';
import { NetworkUserRole } from '@/types/networkUser';
import { logUserLogin, logUserLogout } from '@/services/audit/authAuditService';
import { registerGlobalAuthCleanup } from '@/utils/authStorage';

type SupabaseAuthValue = ReturnType<typeof useSupabaseAuthState>;

const SupabaseAuthContext = createContext<SupabaseAuthValue | undefined>(undefined);

const useSupabaseAuthState = () => {
  const [user, setUser] = useState<StoreUser | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [status, setStatus] = useState<AuthStatus>('booting');
  const [networkId, setNetworkId] = useState<string | null>(null);

  // Controle de concorrência e idempotência
  const isMounted = useRef(true);
  const isProcessing = useRef(false);
  const pendingSession = useRef<Session | null | undefined>(undefined);
  const currentSessionId = useRef<string | null>(null);
  const operationCount = useRef(0);
  const userRef = useRef<StoreUser | null>(null);
  const statusRef = useRef<AuthStatus>('booting');

  const updateUser = (nextUser: StoreUser | null) => {
    userRef.current = nextUser;
    setUser(nextUser);
  };

  const updateStatus = (nextStatus: AuthStatus) => {
    statusRef.current = nextStatus;
    setStatus(nextStatus);
  };

  const loadUserProfile = async (authUser: User, options?: { silent?: boolean; prefetchedNetworkRole?: { role: string; network_id: string } | null }) => {
    const opId = ++operationCount.current;
    const userId = authUser.id;
    console.log(`[AUTH] loadUserProfile started #${opId} for:`, userId);
    
    try {
      const { data: profile, error: profileErr } = await supabase
        .from('profiles')
        .select('id, name, first_name, last_name, nickname, username, avatar_url, cpf, phone, gender, address, is_active, is_master, tenant_id, sales, performance, created_at, updated_at, is_platform_admin')
        .eq('id', userId)
        .maybeSingle();

      if (profileErr) throw profileErr;
      if (!profile) throw new Error('Perfil de usuário não encontrado');

      // Proteção contra "stale async result" e "Signed Out"
      if (!isMounted.current || opId !== operationCount.current || !currentSessionId.current) {
        console.warn(`[AUTH] Discarding stale profile result #${opId}`);
        return;
      }

      if (profile.is_platform_admin) {
        const storeUser: StoreUser = {
          id: profile.id,
          name: profile.name,
          email: authUser.email || '',
          avatar: profile.avatar_url || undefined,
          role: StoreUserRole.STORE_VIEWER,
          permissions: [],
          isActive: profile.is_active,
          isMaster: false,
          isPlatformAdmin: true,
          sales: 0,
          createdAt: profile.created_at,
        };

        updateUser(storeUser);
        setNetworkId(null);
        updateStatus('authenticated');
        return;
      }

      const [roleRes, permissionsRes, customPermsRes, networkRolesRes] = await Promise.all([
        supabase.from('user_roles').select('role').eq('user_id', userId).single(),
        supabase.rpc('get_user_permissions', { _user_id: userId }),
        supabase.from('user_custom_permissions').select('permission, granted').eq('user_id', userId).eq('granted', true),
        options?.prefetchedNetworkRole !== undefined
          ? Promise.resolve({ data: options.prefetchedNetworkRole, error: null })
          : supabase.from('user_network_roles').select('role, network_id').eq('user_id', userId).eq('is_active', true).limit(1).maybeSingle(),
      ]);

      if (!isMounted.current || opId !== operationCount.current || !currentSessionId.current) {
        console.warn(`[AUTH] Discarding stale async result #${opId} after roles fetch`);
        return;
      }

      const permissions = (permissionsRes.data || []).map((p: any) => p.permission as StorePermission);
      const networkRoles = networkRolesRes.data as { role: string; network_id: string } | null;

      const storeUser: StoreUser = {
        id: profile.id,
        name: profile.name,
        firstName: profile.first_name || undefined,
        lastName: profile.last_name || undefined,
        nickname: profile.nickname || undefined,
        email: authUser.email || '',
        phone: profile.phone || undefined,
        cpf: profile.cpf || undefined,
        gender: profile.gender as any,
        address: profile.address as any,
        username: profile.username || undefined,
        avatar: profile.avatar_url || undefined,
        role: (roleRes.data?.role as StoreUserRole) || StoreUserRole.STORE_VIEWER,
        permissions: permissions,
        isActive: profile.is_active,
        isMaster: profile.is_master,
        isPlatformAdmin: false,
        sales: profile.sales || 0,
        createdAt: profile.created_at,
        customPermissions: customPermsRes.data?.map(cp => cp.permission as StorePermission),
        isCustomPermissions: (customPermsRes.data?.length ?? 0) > 0,
        networkRole: networkRoles?.role as NetworkUserRole,
      };

      updateUser(storeUser);
      setNetworkId(networkRoles?.network_id || null);
      updateStatus('authenticated');
    } catch (error) {
      console.error(`[AUTH] Error in loadUserProfile #${opId}:`, error);
      if (isMounted.current && opId === operationCount.current && currentSessionId.current) {
        updateStatus('error');
      }
    }
  };

  const processSession = async (currentSession: Session | null, eventSource: string) => {
    if (isProcessing.current) {
      console.log(`[AUTH] Queueing session from ${eventSource}`);
      pendingSession.current = currentSession;
      return;
    }

    isProcessing.current = true;
    console.log(`[AUTH] Processing session change from ${eventSource}:`, !!currentSession?.user);
    
    const nextSessionId = currentSession?.user?.id || null;
    setSession(currentSession);

    // Eventos repetidos da mesma sessão não devem reiniciar uma carga concluída.
    if (nextSessionId && currentSessionId.current === nextSessionId && userRef.current && statusRef.current === 'authenticated') {
      isProcessing.current = false;
      return;
    }

    currentSessionId.current = nextSessionId;

    if (currentSession?.user) {
      // Se for apenas refresh de token e já temos usuário, apenas atualiza sessão
      if (eventSource === 'event:TOKEN_REFRESHED' && statusRef.current === 'authenticated' && userRef.current) {
        console.log('[AUTH] TOKEN_REFRESHED: Transparent update');
        isProcessing.current = false;
        return;
      }

      // Inicia loading visual apenas se não estivermos autenticados ou se for boot
      if (statusRef.current !== 'authenticated') {
        updateStatus('booting');
      }
      
      await loadUserProfile(currentSession.user, { silent: true });
    } else {
      console.log(`[AUTH] No user from ${eventSource}, unauthenticated`);
      updateUser(null);
      setNetworkId(null);
      updateStatus('unauthenticated');
    }
    
    isProcessing.current = false;

    // Processar próxima sessão na fila se houver
    if (pendingSession.current !== undefined) {
      const next = pendingSession.current;
      pendingSession.current = undefined;
      await processSession(next, 'queued');
    }
  };

  const startBoot = useCallback(async () => {
    console.log('[AUTH] Starting bootstrap...');
    try {
      const { data: { session: initialSession }, error } = await supabase.auth.getSession();
      if (error) console.error('[AUTH] getSession error:', error);
      if (isMounted.current) {
        await processSession(initialSession, 'boot');
      }
    } catch (err) {
      console.error('[AUTH] Bootstrap crash:', err);
      if (isMounted.current) updateStatus('error');
    }
  }, []);

  useEffect(() => {
    isMounted.current = true;
    registerGlobalAuthCleanup();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, currentSession) => {
        console.log(`[AUTH] onAuthStateChange: ${event}`);
        if (!isMounted.current) return;

        if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
          // O callback do Supabase Auth não pode aguardar consultas PostgREST.
          // Agenda o processamento fora do lock interno do cliente de autenticação.
          queueMicrotask(() => {
            if (isMounted.current) void processSession(currentSession, `event:${event}`);
          });
        } else if (event === 'SIGNED_OUT') {
          currentSessionId.current = null;
          operationCount.current++; // Cancela profile loadings em andamento
          pendingSession.current = undefined;
          isProcessing.current = false;
          updateUser(null);
          setSession(null);
          setNetworkId(null);
          updateStatus('unauthenticated');
        }
      }
    );

    startBoot();

    return () => {
      isMounted.current = false;
      subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string, captchaToken?: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email: email.trim().toLowerCase(),
      password: password.trim(),
      ...(captchaToken && { options: { captchaToken } }),
    });

    if (error) throw error;
    if (!data.user) throw new Error('Usuário não encontrado');

    const [isAdminRes, networkRolesRes] = await Promise.all([
      supabase.rpc('is_platform_admin', { _user_id: data.user.id }),
      supabase.from('user_network_roles').select('is_active, role, store_ids, network_id').eq('user_id', data.user.id).eq('is_active', true),
    ]);

    if (!isAdminRes.data) {
      const hasValidAccess = (networkRolesRes.data || []).some((role: any) => 
        ['NETWORK_ADMIN', 'NETWORK_SUPERVISOR'].includes(role.role) || (role.store_ids && role.store_ids.length > 0)
      );
      if (!hasValidAccess) {
        await supabase.auth.signOut({ scope: 'local' });
        throw new Error('PENDING_ACCESS');
      }
    }

    return { data, error: null };
  };

  const signOut = async () => {
    try {
      if (user && networkId) await logUserLogout(user.id, networkId);
      await supabase.auth.signOut();
      const { clearAllAuthSessions } = await import('@/utils/authStorage');
      clearAllAuthSessions();
      updateUser(null);
      setSession(null);
      updateStatus('unauthenticated');
      return { error: null };
    } catch (error: any) {
      toast({ title: 'Erro no logout', description: error.message, variant: 'destructive' });
      return { error };
    }
  };

  // Watchdog: evita ficar preso em 'booting' (tela branca). Refaz uma vez; depois erro recuperável.
  const bootRetryCount = useRef(0);
  useEffect(() => {
    if (status !== 'booting') {
      if (status !== 'error') bootRetryCount.current = 0;
      return;
    }
    const timer = window.setTimeout(() => {
      if (!isMounted.current || statusRef.current !== 'booting') return;
      if (bootRetryCount.current < 1) {
        bootRetryCount.current += 1;
        console.warn('[AUTH] Boot travado; refazendo verificação.');
        isProcessing.current = false;
        void startBoot();
      } else {
        console.error('[AUTH] Boot travado após nova tentativa.');
        updateStatus('error');
      }
    }, 12000);
    return () => window.clearTimeout(timer);
  }, [status]);

  return {
    user,
    session,
    status,
    isLoading: status === 'booting',
    networkId,
    signIn,
    signOut,
    retry: startBoot
  };
};

export const SupabaseAuthProvider = ({ children }: { children: ReactNode }) => {
  const value = useSupabaseAuthState();
  return createElement(SupabaseAuthContext.Provider, { value }, children);
};

export const useSupabaseAuth = () => {
  const context = useContext(SupabaseAuthContext);
  if (!context) {
    throw new Error('useSupabaseAuth must be used within SupabaseAuthProvider');
  }
  return context;
};
